import { useEffect } from "react";

/**
 * Adds reveal-on-scroll animations to every <section> on the page.
 * - Marks sections with [data-reveal] (CSS handles the initial hidden state)
 * - Uses IntersectionObserver to add "is-visible" when the section enters the viewport
 * - Re-scans on route changes via a debounced MutationObserver fallback
 *
 * Performance: rescans are deferred to idle/raf and only triggered by structural
 * mutations (childList), not attribute changes. This avoids forced reflows from
 * the observer reacting to its own setAttribute calls.
 */
export const useScrollReveal = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
    );

    const tagAndObserve = () => {
      // Phase 1 (read-only): collect candidate sections.
      const sections = document.querySelectorAll<HTMLElement>("main section:not([data-reveal])");
      const targets: HTMLElement[] = [];
      sections.forEach((el) => {
        if (el.dataset.skipReveal === "true" || el.closest("[data-skip-reveal='true']")) return;
        targets.push(el);
      });
      if (targets.length === 0) return;

      // Phase 2 (batched writes): mutate attributes inside a single rAF so
      // the browser doesn't interleave style invalidation with the
      // IntersectionObserver's layout reads on each iteration.
      requestAnimationFrame(() => {
        for (const el of targets) {
          el.setAttribute("data-reveal", "");
          if (reduced) {
            el.classList.add("is-visible");
          } else {
            observer.observe(el);
          }
        }
      });
    };

    // Defer the initial scan to idle time so it doesn't compete with LCP.
    const initialScan: (cb: () => void) => number =
      (window as any).requestIdleCallback?.bind(window) ??
      ((cb: () => void) => window.setTimeout(cb, 0));
    initialScan(tagAndObserve);

    // Debounce rescans to avoid forced reflows from rapid DOM mutations.
    const ric: (cb: () => void) => number =
      (window as any).requestIdleCallback?.bind(window) ??
      ((cb: () => void) => window.setTimeout(cb, 200));
    const cic: (id: number) => void =
      (window as any).cancelIdleCallback?.bind(window) ?? window.clearTimeout;

    let scheduled: number | null = null;
    const scheduleRescan = () => {
      if (scheduled !== null) return;
      scheduled = ric(() => {
        scheduled = null;
        tagAndObserve();
      });
    };

    // Observe only <main> (not the entire body) and only react when nodes
    // are actually added — ignore class/attribute changes from our own
    // observer or from animations to prevent reflow loops.
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.addedNodes.length > 0) {
          scheduleRescan();
          return;
        }
      }
    });
    const main = document.querySelector("main") ?? document.body;
    mo.observe(main, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mo.disconnect();
      if (scheduled !== null) cic(scheduled);
    };
  }, []);
};
